import React, { useMemo, useState } from 'react';
import { useTracker } from 'meteor/react-meteor-data';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { formatCalendarDate } from '/imports/utils';
import { css } from '/imports/ui/css';
import { ProjectManagementOwners, ProjectManagementPipelineRow, projectManagementProjectStatus, projectManagementStatusOrder, projectManagementExternalIdLabel, projectManagementMoney as money } from '/imports/api/projectManagement';
import { ProjectManagementLifecycle } from '/imports/api/projectManagementLifecycles';
import { ProjectStatusBadge, ProjectStatusTooltip } from '/imports/ui/ProjectManagementProjectStatus';

const TableStyled = styled.div`
  height: 100%;
  overflow: auto;
  table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  th, td { text-align: left; padding: 0.45rem 0.6rem; border-bottom: 1px solid ${css.grey6}; white-space: nowrap; }
  th { position: sticky; top: 0; background: ${css.white}; z-index: 1; color: ${css.grey2}; font-weight: 600; cursor: pointer; user-select: none; }
  th .sort-arrow { color: ${css.primaryColor}; margin-left: 0.25rem; }
  th.num, td.num { text-align: right; }
  tbody tr { cursor: pointer; }
  tbody tr:hover { background: ${css.grey8}; }
  td.name a { color: inherit; text-decoration: none; }
  td.name a:hover { text-decoration: underline; }
  .name { font-weight: 600; color: ${css.primaryColor}; }
  .muted { color: ${css.grey4}; }
  .sub { font-size: 0.75rem; }
`;

interface Props {
  rows: ProjectManagementPipelineRow[];
  lifecycle: ProjectManagementLifecycle;
  hideCash?: boolean;  // member (stakeholder) view: cash never reaches the client, so drop the columns
  hideOwner?: boolean; // member view: the owner is uniform (e.g. RIC) and not their concern, so drop the column
  nextTaskMode?: boolean; // member view: show 'Next Task' (their soonest open task) instead of 'Next Milestone'
  hideFees?: boolean;     // no RIC money model, or a viewer who is not a fee viewer (the figures never reach them)
}

/** The earliest not-yet-done milestone (no actual date) — drives the "Next Milestone" column. */
const nextMilestone = (row: ProjectManagementPipelineRow) => {
  const open = row.milestones.filter((m) => !m.actualDate && m.plannedDate);
  open.sort((a, b) => new Date(a.plannedDate as Date).getTime() - new Date(b.plannedDate as Date).getTime());
  return open[0];
};

/** The soonest incomplete task (by estimated date) — drives the member view's "Next Task" column. */
const nextTask = (row: ProjectManagementPipelineRow) => {
  const open = (row.tasks || []).filter((t) => t.status !== 'done' && t.estimatedDate);
  open.sort((a, b) => new Date(a.estimatedDate as Date).getTime() - new Date(b.estimatedDate as Date).getTime());
  return open[0];
};

type SortKey = 'name' | 'luminiaId' | 'externalId' | 'status' | 'nextMilestone' | 'cashIn' | 'cashOut' | 'fees' | 'owner';

/** Spreadsheet-style portfolio grid: a row per project with status, next milestone, and cash.
 *  Click a column header to sort (click again to flip); default order is the one the rows arrive in
 *  (status, then portfolio, then name), so sorting by Status ascending returns the grid to it. */
export const ProjectManagementTableView = ({ rows, lifecycle, hideCash, hideOwner, nextTaskMode, hideFees }: Props) => {
  const navigate = useNavigate();
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>();
  // The "next" column is either the next milestone (default) or the next open task (member view).
  const nextForRow = (row: ProjectManagementPipelineRow): { label: string; date: Date } | undefined => {
    if (nextTaskMode) {
      const t = nextTask(row);
      return t?.estimatedDate ? { label: t.label, date: new Date(t.estimatedDate as Date) } : undefined;
    }
    const m = nextMilestone(row);
    return m?.plannedDate ? { label: m.label, date: new Date(m.plannedDate as Date) } : undefined;
  };
  const sorted = useMemo(() => {
    if (!sort) return rows;
    // Sort value per column: strings compare case-insensitively, blanks always sink to the bottom.
    const value = (row: ProjectManagementPipelineRow): string | number | undefined => {
      const p = row.project;
      switch (sort.key) {
        case 'name': return p.name?.toLowerCase();
        // Prefix, then the sequence as a number: padded to a common width so RIC0099 still precedes a
        // widened RIC00100, which a plain string compare would get backwards.
        case 'luminiaId': {
          if (!p.luminiaId) return undefined;
          const m = p.luminiaId.match(/^([A-Za-z]+)(\d+)$/);
          return m ? `${m[1]}${m[2].padStart(9, '0')}` : p.luminiaId.toLowerCase();
        }
        // These ids are numbers written as text (a RIC # of 1980 vs 11591), so sort them as numbers when
        // they are numeric — lexically, 11591 would land before 1980.
        case 'externalId': {
          if (!p.externalId) return undefined;
          const n = Number(p.externalId);
          return Number.isFinite(n) ? n : p.externalId.toLowerCase();
        }
        case 'status': return projectManagementStatusOrder[projectManagementProjectStatus(p)];
        case 'nextMilestone': {
          const next = nextForRow(row);
          return next ? next.date.getTime() : undefined;
        }
        case 'cashIn': return row.cash.inActual;
        case 'cashOut': return row.cash.outActual;
        case 'fees': return row.fees?.actual;
        case 'owner': return p.owner?.toLowerCase();
        default: return undefined;
      }
    };
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av === undefined || av === '') return 1;
      if (bv === undefined || bv === '') return -1;
      if (av < bv) return -sort.dir;
      if (av > bv) return sort.dir;
      return 0;
    });
  }, [rows, sort, nextTaskMode]);
  const toggleSort = (key: SortKey) => setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  const th = (key: SortKey, label: string, num?: boolean) => (
    <th className={num ? 'num' : undefined} onClick={() => toggleSort(key)}>
      {label}
      {sort?.key === key && <span className="sort-arrow">{sort.dir === 1 ? '▲' : '▼'}</span>}
    </th>
  );
  // Owner is a reference now; the registry rides along on the pipeline publication.
  const owners = useTracker(() => ProjectManagementOwners.find({ active: { $ne: false } }).fetch(), []);
  const ownerName = (ownerId?: string) => (ownerId ? owners.find((o) => o._id === ownerId)?.name : undefined);

  return (
    <TableStyled>
      <table>
        <thead>
          <tr>
            {th('name', strings.Name())}
            {th('luminiaId', strings.LuminiaId())}
            {/* The vertical names its own id — "RIC #" here, "External ID" where a lifecycle declares none. */}
            {th('externalId', projectManagementExternalIdLabel(lifecycle.key))}
            {th('status', strings.Status())}
            {th('nextMilestone', nextTaskMode ? strings.NextTask() : strings.NextMilestone())}
            {!hideCash && th('cashIn', strings.CashSources(), true)}
            {!hideCash && th('cashOut', strings.CashUses(), true)}
            {!hideCash && !hideFees && th('fees', strings.TotalFees(), true)}
            {!hideOwner && th('owner', strings.Owner())}
          </tr>
        </thead>
        <tbody>
          {
            sorted.map((row) => {
              const p = row.project;
              const next = nextForRow(row);
              return (
                <tr key={p._id} onClick={() => navigate(`/project-management/projects/${p._id}`)}>
                  {/* A real link, so the browser's own open-in-new-tab / middle-click work; the row click
                      still opens it in place. The link stops its click so the row does not navigate twice. */}
                  <td className="name"><Link to={`/project-management/projects/${p._id}`} onClick={(e) => e.stopPropagation()}>{p.name}</Link></td>
                  <td>{p.luminiaId || <span className="muted">—</span>}</td>
                  <td>{p.externalId || <span className="muted">—</span>}</td>
                  <td><ProjectStatusBadge project={p} /></td>
                  <td>{next ? `${next.label} · ${formatCalendarDate(next.date)}` : <span className="muted">—</span>}</td>
                  {!hideCash && <td className="num">{money(row.cash.inActual)}</td>}
                  {!hideCash && <td className="num">{money(row.cash.outActual)}</td>}
                  {/* Same shape as the project page's "Total fees" stat: actual first, estimated beneath. */}
                  {!hideCash && !hideFees && <td className="num">{money(row.fees?.actual)}<div className="muted sub">{strings.Estimated()}: {money(row.fees?.estimated)}</div></td>}
                  {!hideOwner && <td>{ownerName(p.ownerId) || <span className="muted">—</span>}</td>}
                </tr>
              );
            })
          }
          {!rows.length && <tr><td colSpan={5 + (hideCash ? 0 : (hideFees ? 2 : 3)) + (hideOwner ? 0 : 1)} className="muted">—</td></tr>}
        </tbody>
      </table>
      <ProjectStatusTooltip />
    </TableStyled>
  );
};
